import React from 'react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, Transition } from '@headlessui/react';
import { Mail, Shield, Check, User } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Role } from '../../../services/memberService';
import { useMemberLabels } from './memberLabels';

interface MemberInviteFormProps {
  name: string;
  email: string;
  role: string;
  emailError: string | null;
  assignableRoles: Role[];
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

/** Inline form for inviting a new member (name, email, role picker). */
export const MemberInviteForm: React.FC<MemberInviteFormProps> = ({
  name,
  email,
  role,
  emailError,
  assignableRoles,
  onNameChange,
  onEmailChange,
  onRoleChange,
  onSubmit,
  onCancel,
}) => {
  const { t } = useLanguage();
  const { roleLabel } = useMemberLabels(assignableRoles);
  const selectedRole = assignableRoles.find((r) => r.name === role);

  return (
    <div
      data-test-id="member-invite-form"
      className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-fadeIn"
    >
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1 w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settingsMember.fields.fullName', undefined, 'Full Name')}</label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              data-test-id="member-invite-name-input"
              type="text"
              required
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 pl-10"
              placeholder={t('settingsMember.fields.fullNamePlaceholder', undefined, 'Jane Doe')}
            />
          </div>
        </div>
        <div className="flex-1 w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settingsMember.fields.emailAddress')}</label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              data-test-id="member-invite-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              aria-invalid={emailError ? true : undefined}
              className={cn(
                'block w-full sm:text-sm rounded-md p-2 pl-10',
                emailError
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
              )}
              placeholder={t('settingsMember.fields.emailPlaceholder')}
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settingsMember.fields.role')}</label>
          <Listbox value={role} onChange={onRoleChange}>
            <div className="relative">
              <ListboxButton
                data-test-id="member-role-select"
                className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm p-2 hover:bg-gray-100 transition-colors duration-200"
              >
                <span className="block truncate">{selectedRole?.display_name || roleLabel(role)}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <Shield className="h-4 w-4 text-gray-400" />
                </span>
              </ListboxButton>
              <Transition
                as={React.Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm cursor-pointer">
                  {assignableRoles.map((r) => (
                    <ListboxOption
                      key={r.name}
                      value={r.name}
                      className={({ focus }) =>
                        cn(
                          'relative cursor-pointer select-none py-2 pl-10 pr-4',
                          focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'
                        )
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                            {r.display_name || roleLabel(r.name)}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            type="button"
            data-test-id="member-invite-cancel-button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
          >
            {t('auth.common.cancel')}
          </button>
          <button
            data-test-id="member-send-invite-button"
            type="submit"
            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 w-full sm:w-auto"
          >
            {t('settingsMember.actions.sendInvite')}
          </button>
        </div>
      </form>
    </div>
  );
};
